// /vms/verify — 證據匯出驗證（ls-loop MV.5）：不用登入、檔案不上傳。
// 選匯出的 ZIP → 瀏覽器解開、逐檔重算 SHA-256 對 manifest、用 GET /v3/exports/keys 的公鑰驗 manifest 簽章。

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTimestampUs } from '../../lib/format';
import { verifyExport, type VerifyResult } from '../../lib/vms/evidence';
import './vms.css';

export default function VerifyPage() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<VerifyResult | null>(null);
  const [fileName, setFileName] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setBusy(true);
    setRes(null);
    setErr(null);
    setFileName(f.name);
    try {
      setRes(await verifyExport(f));
    } catch {
      setErr(t('vmsVerify.keysFailed'));
    } finally {
      setBusy(false);
    }
  };

  const m = res?.manifest;
  return (
    <main className="vms-verify">
      <h1>{t('vmsVerify.title')}</h1>
      <p className="text-secondary">{t('vmsVerify.subtitle')}</p>
      <label className="btn-primary vms-verify-pick">
        {t('vmsVerify.pick')}
        <input type="file" accept=".zip,application/zip" onChange={(e) => void onFile(e.target.files?.[0])} style={{ display: 'none' }} data-testid="vf-file" />
      </label>
      {fileName && <p className="text-secondary vms-lb-msg">{fileName}</p>}
      {busy && <p role="status">{t('vmsVerify.checking')}</p>}
      {err && <p className="text-danger" role="alert">{err}</p>}
      {res && (
        <section className="card" data-testid="vf-result" data-ok={res.ok}>
          <h2 className={res.ok ? 'vms-verify-ok' : 'vms-verify-bad'} data-testid="vf-verdict">
            {res.ok ? t('vmsVerify.ok') : res.error ? t(`vmsVerify.err_${res.error}`) : t('vmsVerify.bad')}
          </h2>
          {!res.error && (
            <>
              <p data-testid="vf-sig">
                {res.signatureOk ? t('vmsVerify.sigOk') : res.keyFound ? t('vmsVerify.sigBad') : t('vmsVerify.keyUnknown')}
              </p>
              {m && (
                <p className="text-secondary vms-lb-msg">
                  {t('vmsVerify.meta', {
                    id: m.export_id,
                    account: m.account || m.exported_by,
                    at: formatTimestampUs(m.created_at_us, { timeZone: m.time_zone }),
                    from: formatTimestampUs(m.window?.from_us, { timeZone: m.time_zone }),
                    to: formatTimestampUs(m.window?.to_us, { timeZone: m.time_zone }),
                  })}
                  {m.reason ? ` · ${t('vmsVerify.reason', { reason: m.reason })}` : ''}
                </p>
              )}
              <ul className="vms-verify-files" data-testid="vf-files">
                {res.files.map((f) => (
                  <li key={f.name} data-ok={f.ok}>
                    <span className={`vms-ov-health vms-ov-health--${f.ok ? 'ok' : 'fail'}`}>
                      <span className="vms-ov-dot" aria-hidden />
                      {f.name}
                    </span>
                    <span className="text-secondary vms-lb-msg"> {f.ok ? t('vmsVerify.fileOk') : t(`vmsVerify.file_${f.reason}`)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}
    </main>
  );
}
